/**
 * @zendir/ui - Theme Configuration
 *
 * Easy configuration for default theme settings.
 * Import and use in your app root to set defaults.
 */

import type { ThemeVariant, ThemeMode } from "./ThemeProvider";

export interface ThemeConfig {
  /** Default theme variant */
  defaultTheme?: ThemeVariant;
  /** Default theme mode */
  defaultMode?: ThemeMode;
  /** Persist theme selection to localStorage */
  persist?: boolean;
}

/**
 * Default theme configuration
 *
 * Available themes:
 * - 'hybrid' - Zen (Hybrid): default; Astro status colors with purple accents
 * - 'astro' - Astro UX (U.S. Space Force) compliant theme
 * - 'purple-hue' - Zen (Purple Hue) theme with modern aesthetics
 * - 'transparent' / 'transparent-bold' / 'transparent-minimal' - Glassmorphic themes
 */
export const DEFAULT_THEME_CONFIG: ThemeConfig = {
  defaultTheme: "hybrid", // Zen (Hybrid) is the default
  defaultMode: "dark",
  persist: true,
};

/**
 * Get theme configuration from environment or use defaults
 */
export function getThemeConfig(): ThemeConfig {
  // Allow override via environment variable or build-time config
  if (typeof window !== "undefined") {
    const envTheme = (window as any).__ZENDIR_UI_THEME__;
    if (
      envTheme === "astro" ||
      envTheme === "purple-hue" ||
      envTheme === "hybrid" ||
      envTheme === "nebula"
    ) {
      return {
        ...DEFAULT_THEME_CONFIG,
        defaultTheme: envTheme,
      };
    }
  }

  return DEFAULT_THEME_CONFIG;
}

/**
 * Set default theme at build/runtime
 *
 * @example
 * ```tsx
 * // In your app root
 * import { setDefaultTheme } from '@zendir/ui/react/theme/config';
 *
 * // Zen (Hybrid) is the default; override if needed:
 * setDefaultTheme('hybrid');
 * setDefaultTheme('purple-hue');
 * setDefaultTheme('astro');
 * ```
 */
export function setDefaultTheme(theme: ThemeVariant): void {
  if (typeof window !== "undefined") {
    (window as any).__ZENDIR_UI_THEME__ = theme;
    // Also update localStorage if persist is enabled
    localStorage.setItem("zendir-ui-theme", theme);
  }
}
